import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, Users, Tablet, ArrowRight, Store, GraduationCap, Video, FlaskConical, PenTool, Mail, BarChart3, Code2, Loader2 } from "lucide-react";
import { api, apiError } from "../lib/api";
import { OFFRES_LIST } from "../offres";
import { fmt, ttc } from "../components/offres/OffrePage";
import { PlaquetteEmailForm } from "../components/PlaquetteEmailForm";

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/plaquette.pdf`;
const DEVIS_CATEGORY = "Solutions sur devis";
const UNIVERS = [
  { icon: Store, t: "Catalogue de produits digitaux", d: "Guides, modèles, logiciels et packages prêts à l'emploi.", to: "/catalogue" },
  { icon: GraduationCap, t: "Formations médicales & LMS", d: "Cours en ligne, parcours certifiants, quiz et attestations.", to: "/cours" },
  { icon: Video, t: "Webinaires & Téléconsultation", d: "Événements en direct, replays et visioconférence médicale sécurisée.", to: "/webinaires" },
  { icon: FlaskConical, t: "Recherche scientifique", d: "Projets, appels à candidatures, rédaction assistée et statistiques.", to: "/recherche-scientifique" },
  { icon: PenTool, t: "Atelier d'écriture & Édition Santé", d: "Rédaction médicale et littéraire assistée par IA, mise en page et couvertures.", to: "/atelier-ecriture" },
  { icon: Mail, t: "Courriers institutionnels GIC-IA", d: "Rédaction, signature électronique et suivi des courriers par service.", to: "/courriers" },
  { icon: BarChart3, t: "Pilotage hospitalier", d: "Tableau de bord hospitalier, PMSI & couverture, Direction générale, qualité patient.", to: "/pilotage" },
  { icon: Code2, t: "Prestations & développement", d: "Développement sur mesure, déploiement SIH, formation des équipes et support.", to: "/developpement-informatique" },
];

export function PlaquetteButton({ className = "", testid = "plaquette-download-btn" }) {
  return (
    <a href={PDF_URL} download data-testid={testid} className={`inline-flex items-center gap-2 rounded-full bg-[#0369A1] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#075985] ${className}`}>
      <FileDown size={16} /> Télécharger la plaquette en PDF
    </a>
  );
}

export default function Plaquette() {
  const [solutions, setSolutions] = useState(null);
  useEffect(() => {
    api.get("/products", { params: { category: DEVIS_CATEGORY } })
      .then(({ data }) => setSolutions(data))
      .catch((e) => { toast.error(apiError(e, "Chargement impossible")); setSolutions([]); });
  }, []);

  return (
    <div data-testid="plaquette-page">
      <section className="bg-[#E0F2FE]">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#0369A1]">Plaquette commerciale</p>
            <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-[#0C4A6E] sm:text-5xl">Vitalis</h1>
            <p className="mt-2 font-display text-lg font-bold text-[#0369A1] md:text-xl">Patient Vitalis Care & Vitalis Digital Store</p>
            <p className="mt-4 max-w-xl text-base text-slate-700">Le système d'information de santé pensé pour l'Afrique francophone : du cabinet médical au CHU, des solutions clés en main, hébergées, accompagnées et évolutives.</p>
            <div className="mt-6 flex flex-wrap gap-3"><PlaquetteButton /><Link to="/nos-offres" className="inline-flex items-center gap-2 rounded-full border border-[#7DC4E8] bg-white px-6 py-3 text-sm font-semibold text-[#0369A1] hover:bg-[#F8FBFE]">Nos offres <ArrowRight size={15} /></Link></div>
            <p className="mt-5 text-xs text-slate-600">Cabinet CARIS Santé · Dr ANON Barthelemy, Médecin DIM, Chercheur en Informatique médicale</p>
          </div>
          <img src="/images/sih-sante.jpg" alt="Vitalis — hôpital connecté" className="w-full rounded-2xl border border-[#7DC4E8] shadow-sm" onError={(e) => { e.currentTarget.src = "/images/cabinet-medical.jpg"; }} />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="plaquette-pvc">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]">Patient Vitalis Care (PVC)</p>
        <h2 className="mt-1 font-display text-2xl font-bold text-[#0C4A6E] sm:text-3xl">Le SIH clés en main, du cabinet au CHU</h2>
        <p className="mt-3 max-w-3xl text-sm text-slate-600">Chaque offre comprend les licences utilisateurs (3 appareils par utilisateur), une tablette de qualité livrée configurée, le support technique, les mises à jour et l'hébergement cloud sécurisé. Abonnement mensuel, TVA 18 % en sus.</p>
        <div className="mt-8 divide-y divide-[#BAE0F5] rounded-2xl border border-[#7DC4E8] bg-white">
          {OFFRES_LIST.map((o) => (
            <div key={o.slug} className="grid gap-4 p-5 sm:grid-cols-[160px_1fr_auto] sm:items-center" data-testid={`plaquette-offre-${o.slug}`}>
              <img src={o.image} alt={`Vitalis ${o.nom}`} loading="lazy" className="aspect-[3/2] w-full rounded-xl border border-[#7DC4E8] object-cover" />
              <div>
                <h3 className="font-display text-base font-bold text-navy">Vitalis {o.nom}</h3>
                <p className="mt-0.5 text-xs text-slate-500">Niveau {o.niveau} · <Users size={11} className="inline" /> {o.licences} licences · <Tablet size={11} className="inline" /> tablette + support inclus</p>
                <p className="mt-2 text-xs text-slate-600"><b>Modules :</b> {o.modules.map((m) => m.title).join(", ")}</p>
              </div>
              <div className="sm:text-right">
                <p className="font-display text-xl font-bold text-[#0369A1]">{fmt(o.prixHt)} <span className="text-xs">FCFA HT/mois</span></p>
                <p className="text-xs text-slate-500">soit {fmt(ttc(o.prixHt, o.tva))} FCFA TTC</p>
                <Link to={`/offres/${o.slug}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#0369A1] hover:underline">Voir l'offre <ArrowRight size={12} /></Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="plaquette-vds">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]">Vitalis Digital Store (VDS)</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-[#0C4A6E] sm:text-3xl">La boutique et le portail des professionnels de santé</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {UNIVERS.map(({ icon: Icon, t, d, to }) => (
              <Link key={t} to={to} className="rounded-2xl border border-[#7DC4E8] bg-[#F8FBFE] p-5 transition-transform duration-200 hover:-translate-y-0.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E0F2FE] text-[#0369A1]"><Icon size={20} /></div>
                <p className="mt-3 text-sm font-bold text-navy">{t}</p>
                <p className="mt-1 text-xs text-slate-600">{d}</p>
              </Link>
            ))}
          </div>

          <h3 className="mt-12 font-display text-lg font-bold text-navy">Solutions sur devis</h3>
          <p className="mt-1 text-xs text-slate-500">Plateformes développées et déployées sur mesure — devis personnalisé après étude de vos besoins.</p>
          {!solutions ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ciel" /></div> : (
            <div className="mt-4 grid gap-4 sm:grid-cols-3" data-testid="plaquette-devis-grid">
              {solutions.map((p) => (
                <Link key={p.product_id} to={`/produit/${p.product_id}`} className="flex flex-col rounded-2xl border border-[#7DC4E8] bg-white p-5 transition-transform duration-200 hover:-translate-y-0.5" data-testid={`plaquette-devis-${p.product_id}`}>
                  <p className="font-display text-sm font-bold text-navy">{p.title}</p>
                  <p className="mt-2 flex-1 text-xs text-slate-600">{(p.short_description || "").split("\n")[0]}</p>
                  <span className="mt-4 inline-flex w-fit rounded-full bg-[#E0F2FE] px-3 py-1 text-[11px] font-semibold text-[#0369A1]">Sur devis</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#E0F2FE]">
        <div className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6">
          <h2 className="font-display text-2xl font-bold text-[#0C4A6E] sm:text-3xl">Parlons de votre projet</h2>
          <p className="mt-3 text-sm text-slate-700"><b>Cabinet CARIS Santé</b><br />Dr ANON Barthelemy · Médecin DIM · Chercheur en Informatique médicale</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3"><PlaquetteButton testid="plaquette-download-btn-bottom" /><Link to="/prendre-rendez-vous" className="inline-flex items-center gap-2 rounded-full border border-[#7DC4E8] bg-white px-6 py-3 text-sm font-semibold text-[#0369A1] hover:bg-[#F8FBFE]">Prendre rendez-vous</Link></div>
          <div className="mx-auto mt-8 max-w-2xl rounded-2xl border border-[#7DC4E8] bg-white p-6 text-left shadow-sm">
            <h3 className="font-display text-base font-bold text-navy">Recevoir la plaquette par email</h3>
            <p className="mb-4 mt-1 text-xs text-slate-500">Nous vous envoyons le PDF directement dans votre boîte mail.</p>
            <PlaquetteEmailForm source="page-plaquette" />
          </div>
        </div>
      </section>
    </div>
  );
}
